import { useEffect, useState } from "react";
import { RotateCcw, Fuel } from "lucide-react";
import { Card, CardAction, CardContent, CardHeader, CardTitle } from "./components/ui/card";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";

type PromptMode = "android" | "ios" | "desktop";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
}

export default function MileageCalculator() {
  const [mpg, setMpg] = useState<number | "">("");
  const [rangeLeft, setRangeLeft] = useState<number | "">("");
  const [distance, setDistance] = useState<number | "">("");
  const [promptMode, setPromptMode] = useState<PromptMode | null>(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    const standaloneMode =
      window.matchMedia("(display-mode: standalone)").matches ||
      (window.navigator as Navigator & { standalone?: boolean }).standalone === true;

    if (standaloneMode) {
      return;
    }

    const userAgent = window.navigator.userAgent.toLowerCase();
    const isIOS = /iphone|ipad|ipod/.test(userAgent);
    const isAndroid = /android/.test(userAgent);
    const isMobile = isIOS || isAndroid;

    if (isIOS) {
      setPromptMode("ios");
      setShowPrompt(true);
      return;
    }

    if (isAndroid) {
      setPromptMode("android");
      setShowPrompt(true);
    }

    if (!isMobile) {
      setPromptMode("desktop");
      setShowPrompt(true);
      return;
    }

    const handleBeforeInstallPrompt = (event: Event) => {
      const installEvent = event as BeforeInstallPromptEvent;
      installEvent.preventDefault();
      setDeferredPrompt(installEvent);
      setPromptMode("android");
      setShowPrompt(true);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    };
  }, []);

  const dismissPrompt = () => {
    setShowPrompt(false);
  };

  const handleAndroidInstall = async () => {
    if (!deferredPrompt) {
      return;
    }

    await deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;

    if (choice.outcome === "accepted") {
      setShowPrompt(false);
    }

    setDeferredPrompt(null);
  };

  const parseNumber = (value: number | ""): number | null => {
    return value === "" || isNaN(value) ? null : value;
  };

  const hasAnyValue = mpg !== "" || rangeLeft !== "" || distance !== "";
  const allFieldsFilled = mpg !== "" && rangeLeft !== "" && distance !== "";
  const invalidCls = (empty: boolean) =>
    hasAnyValue && empty ? "border-red-500 shadow-[3px_3px_0px_0px_#ef4444]" : undefined;

  const parsedMpg = parseNumber(mpg);
  const parsedDistance = parseNumber(distance);
  const parsedRange = parseNumber(rangeLeft);

  const LITRES_PER_GALLON = 4.54609;

  const fuelRequiredRaw: number | null =
    parsedMpg && parsedDistance && parsedRange !== null
      ? ((parsedDistance - parsedRange) / parsedMpg) * LITRES_PER_GALLON
      : null;

  const fuelRequired: string =
    fuelRequiredRaw === null
      ? "0"
      : fuelRequiredRaw <= 0
      ? "None needed"
      : `${fuelRequiredRaw.toFixed(1)} litres`;

  // Dont show decimal place if its .0
  const fmt = (n: number) => n.toFixed(1).replace(/\.0$/, "");

  const fuelSurplus: string | null = (() => {
    if (fuelRequiredRaw === null || fuelRequiredRaw >= 0) return null;
    const litres = fmt(Math.abs(fuelRequiredRaw));
    if (litres === "0") return null;
    const miles = fmt(Math.abs((parsedRange ?? 0) - (parsedDistance ?? 0)));
    return `Surplus of ${litres} litres / ${miles} miles`;
  })();

  const willMakeIt: boolean | null =
    parsedRange && parsedDistance
      ? parsedRange >= parsedDistance
      : null;

  const mpgWarning =
    parsedMpg !== null && (parsedMpg < 10 || parsedMpg > 150)
      ? parsedMpg < 10
        ? "⚠ That seems low — double-check your MPG figure."
        : "⚠ That seems high — double-check your MPG figure."
      : null;

  const rangeWarning =
    parsedRange !== null && parsedRange > 800
      ? "⚠ That's an unusually large range — double-check the value."
      : null;

  const distanceWarning =
    parsedDistance !== null && parsedDistance > 1000
      ? "⚠ That's an unusually long journey — double-check the distance."
      : null;

  const shortcutHint = /mac/i.test(window.navigator.platform) ? "Cmd+D" : "Ctrl+D";

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#fdf8ee] p-4 pb-24">
      <Card className="w-full max-w-md">
        <CardHeader className="border-b-2 border-foreground pb-4">
          <CardTitle className="flex items-center gap-2">
            <Fuel size={18} className="text-primary" />
            Fuel Calculator
          </CardTitle>
          <CardAction>
            <button
              type="button"
              onClick={() => { setMpg(""); setRangeLeft(""); setDistance(""); }}
              aria-label="Reset fields"
              className="border-2 border-foreground p-1.5 bg-card shadow-[2px_2px_0px_0px_#0f0e9f] hover:shadow-none hover:translate-x-[2px] hover:translate-y-[2px] transition-all flex items-center justify-center"
            >
              <RotateCcw size={15} />
            </button>
          </CardAction>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="mpg">Average MPG of vehicle</Label>
            <Input
              id="mpg"
              type="number"
              placeholder="e.g. 40"
              className={invalidCls(mpg === "")}
              value={mpg}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setMpg(e.target.value === "" ? "" : Number(e.target.value))
              }
              min={0}
            />
            <p className="text-xs text-muted-foreground">Find this in your vehicle's instrument cluster — usually shown as "MPG" or "Avg Fuel Economy" in the driver info menu.</p>
            {mpgWarning && <p className="text-xs font-semibold text-amber-600">{mpgWarning}</p>}
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="range">Range Left (miles)</Label>
            <Input
              id="range"
              type="number"
              placeholder="e.g. 50"
              className={invalidCls(rangeLeft === "")}
              value={rangeLeft}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setRangeLeft(e.target.value === "" ? "" : Number(e.target.value))
              }
              min={0}
            />
            <p className="text-xs text-muted-foreground">Check your instrument cluster for "Range" or "Miles to Empty" — this updates automatically based on current fuel level.</p>
            {rangeWarning && <p className="text-xs font-semibold text-amber-600">{rangeWarning}</p>}
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="distance">Distance to Destination (miles)</Label>
            <Input
              id="distance"
              type="number"
              placeholder="e.g. 120"
              className={invalidCls(distance === "")}
              value={distance}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setDistance(e.target.value === "" ? "" : Number(e.target.value))
              }
              min={0}
            />
            <p className="text-xs text-muted-foreground">Enter the full journey distance, including any planned stop-offs — not just the final destination.</p>
            {distanceWarning && <p className="text-xs font-semibold text-amber-600">{distanceWarning}</p>}
          </div>

          <div className={`mt-2 border-2 border-foreground p-4 flex flex-col gap-2 transition-colors shadow-[4px_4px_0px_0px_#0f0e9f] ${allFieldsFilled ? "bg-primary" : "bg-muted"}`}>
            {!allFieldsFilled ? (
              <p className="text-sm text-muted-foreground">Fill in all fields above to see results.</p>
            ) : (
              <>
                <p className="text-sm font-bold text-primary-foreground">
                  Fuel Required: {fuelRequired}
                  
                </p>
                {willMakeIt !== null && (
                  <div className={`mt-1 flex items-center gap-2 rounded-sm px-3 py-2 ${willMakeIt ? "bg-lime-400/10 border-2 border-lime-300" : "bg-amber-400/10 border-2 border-amber-300"}`}>
                    <span className="text-xl leading-none">{willMakeIt ? "😎" : "😬"}</span>
                    <span className={`text-sm font-bold ${willMakeIt ? "text-lime-300" : "text-amber-300"}`}>
                      {willMakeIt ? "You'll make it!" : "You'll need to refuel"}
                    </span>
                  </div>
                )}

                {fuelSurplus && (
                    <span className="block text-xs font-normal text-primary-foreground mt-0.5">{fuelSurplus}</span>
                  )}
              </>
            )}
          </div>

          <p className="text-xs text-muted-foreground text-right">
            Authors: Jon P, Adam L
          </p>
        </CardContent>
      </Card>

      {showPrompt && promptMode && (
        <div className="fixed inset-x-4 bottom-4 z-50 mx-auto w-full max-w-md bg-primary text-primary-foreground border-2 border-foreground p-4 shadow-[6px_6px_0px_0px_#000]">
          <div className="flex items-start gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center border-2 border-primary-foreground text-primary-foreground">
              <Fuel size={18} />
            </div>
            <div className="flex-1">
              {promptMode === "android" && (
                <>
                  <p className="text-sm font-semibold">Get instant access on every trip</p>
                  <p className="mt-1 text-sm text-primary-foreground/80">
                    {deferredPrompt
                      ? "Add VQS Fuel to your home screen for one-tap access — no browser needed."
                      : "Open your browser menu and tap \"Add to Home screen\" for one-tap access next time."}
                  </p>
                  <div className="mt-3 flex items-center justify-end gap-2">
                      <button
                        type="button"
                        onClick={dismissPrompt}
                        className="border-2 border-primary-foreground px-3 py-1.5 text-sm text-primary-foreground shadow-[2px_2px_0px_0px_rgba(255,255,255,0.4)] hover:shadow-none hover:translate-x-[2px] hover:translate-y-[2px] transition-all"
                      >
                        Not now
                      </button>
                      {deferredPrompt && (
                        <button
                          type="button"
                          onClick={handleAndroidInstall}
                          className="border-2 border-primary-foreground bg-primary-foreground px-3 py-1.5 text-sm font-bold text-primary shadow-[2px_2px_0px_0px_rgba(255,255,255,0.4)] hover:shadow-none hover:translate-x-[2px] hover:translate-y-[2px] transition-all"
                        >
                          Add to Home Screen
                        </button>
                      )}
                    </div>
                </>
              )}

              {promptMode === "ios" && (
                <>
                  <p className="text-sm font-semibold">Get instant access on every trip</p>
                  <p className="mt-1 text-sm text-primary-foreground/80">
                    Tap the <span className="font-bold">Share</span> button{" "}
                    <span className="font-mono text-base leading-none" aria-hidden>⎙</span>{" "}
                    at the bottom of your browser, then select{" "}
                    <span className="font-bold">Add to Home Screen</span>.
                  </p>
                  <div className="mt-3 flex items-center justify-end">
                    <button
                      type="button"
                      onClick={dismissPrompt}
                      className="border-2 border-primary-foreground px-3 py-1.5 text-sm text-primary-foreground shadow-[2px_2px_0px_0px_rgba(255,255,255,0.4)] hover:shadow-none hover:translate-x-[2px] hover:translate-y-[2px] transition-all"
                    >
                      Not now
                    </button>
                  </div>
                </>
              )}

              {promptMode === "desktop" && (
                <>
                  <p className="text-sm font-semibold">Bookmark for quick access</p>
                  <p className="mt-1 text-sm text-primary-foreground/80">
                    Press <span className="font-bold">{shortcutHint}</span> to bookmark this page so you can get back to it instantly before any trip.
                  </p>
                  <div className="mt-3 flex items-center justify-end">
                    <button
                      type="button"
                      onClick={dismissPrompt}
                      className="border-2 border-primary-foreground px-3 py-1.5 text-sm text-primary-foreground shadow-[2px_2px_0px_0px_rgba(255,255,255,0.4)] hover:shadow-none hover:translate-x-[2px] hover:translate-y-[2px] transition-all"
                    >
                      Not now
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
